/**
 * Hook for canvas mouse event handling
 * Extracted from BaseCanvas.tsx to reduce component complexity
 */

import React, { useCallback, useState } from 'react'
import { TOOL_ADD_PLAYER, TOOL_DRAW, TOOL_ERASE, TOOL_FILL } from '@/constants'
import { snapToGrid } from '@/utils/GridSnap.utils'
import type { DrawingState } from '@/types'
import type { FieldCoordinateSystem } from '@/utils'
import type { CanvasZoomPanHandlers } from '../BaseCanvas.types'

interface UseCanvasMouseHandlersOptions {
	drawingState: DrawingState
	coordSystem: FieldCoordinateSystem
	zoom: number
	panX: number
	panY: number
	isPanning: boolean
	panMode: 'spacebar' | 'middleMouse' | null
	zoomPanHandlers: CanvasZoomPanHandlers
}

interface UseCanvasMouseHandlersReturn {
	cursorPosition: { x: number; y: number } | null
	cursorFeetPosition: { x: number; y: number } | null
	isOverCanvas: boolean
	handleMouseMove: (e: React.MouseEvent<HTMLDivElement>, whiteboardRef: React.RefObject<HTMLDivElement | null>) => void
	handleMouseEnter: () => void
	handleMouseLeave: () => void
	getCursorStyle: () => string
	shouldShowCustomCursor: () => boolean
}

/**
 * Manages mouse interactions: cursor position, hover state, cursor style
 */
export function useCanvasMouseHandlers({
										   drawingState,
										   coordSystem,
										   zoom,
										   panX,
										   panY,
										   isPanning,
										   panMode,
										   zoomPanHandlers,
									   }: UseCanvasMouseHandlersOptions): UseCanvasMouseHandlersReturn {
	const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null)
	const [isOverCanvas, setIsOverCanvas] = useState(false)
	const [cursorFeetPosition, setCursorFeetPosition] = useState<{ x: number; y: number } | null>(null)

	const handleMouseMove = useCallback(
		(e: React.MouseEvent<HTMLDivElement>, whiteboardRef: React.RefObject<HTMLDivElement | null>) => {
			if (!whiteboardRef.current) return
			const rect = whiteboardRef.current.getBoundingClientRect()
			const screenX = e.clientX - rect.left
			const screenY = e.clientY - rect.top

			// Apply grid snapping to cursor position when add player tool is active
			if (drawingState.tool === TOOL_ADD_PLAYER) {
				const feetCoords = coordSystem.screenToFeet(screenX, screenY, zoom, panX, panY)
				const snappedCoords = snapToGrid(feetCoords.x, feetCoords.y)
				const snappedScreen = coordSystem.feetToScreen(
					snappedCoords.x,
					snappedCoords.y,
					zoom,
					panX,
					panY,
				)
				setCursorPosition({
					x: snappedScreen.x,
					y: snappedScreen.y,
				})
				// Track feet position for alignment guidelines
				setCursorFeetPosition(snappedCoords)
			} else {
				setCursorPosition({
					x: screenX,
					y: screenY,
				})
				setCursorFeetPosition(null)
			}

			// Delegate pan handling to zoom/pan hook
			zoomPanHandlers.handleMouseMove(e)
		},
		[drawingState.tool, coordSystem, zoom, panX, panY, zoomPanHandlers],
	)

	const handleMouseEnter = useCallback(() => {
		setIsOverCanvas(true)
	}, [])

	const handleMouseLeave = useCallback(() => {
		setIsOverCanvas(false)
		setCursorPosition(null)
		setCursorFeetPosition(null)
	}, [])

	const getCursorStyle = useCallback(() => {
		if (isPanning) return 'grabbing'
		if (panMode === 'spacebar' && zoom > 1) return 'grab'

		switch (drawingState.tool) {
			case TOOL_ERASE:
			case TOOL_DRAW:
			case TOOL_FILL:
			case TOOL_ADD_PLAYER:
				return 'none'
			default:
				return 'default'
		}
	}, [isPanning, panMode, zoom, drawingState.tool])

	const shouldShowCustomCursor = useCallback(() => {
		return !isPanning && panMode !== 'spacebar'
	}, [isPanning, panMode])

	return {
		cursorPosition,
		cursorFeetPosition,
		isOverCanvas,
		handleMouseMove,
		handleMouseEnter,
		handleMouseLeave,
		getCursorStyle,
		shouldShowCustomCursor,
	}
}
